/**
 * The page list of a report (slide deck or document): numbered pages, each a
 * grid, cover or contents page. Select, add, rename (double-click), move,
 * change layout and delete.
 */

import { useState, type ReactNode } from 'react';

import type { Page } from './reports-api.ts';

const LAYOUT_LABEL: Record<string, string> = {
  grid: 'Widgets',
  cover: 'Cover',
  contents: 'Contents',
};

export function ReportPages({
  pages,
  active,
  editable,
  onSelect,
  onChange,
}: {
  pages: Page[];
  active: number;
  editable: boolean;
  onSelect: (index: number) => void;
  onChange: (pages: Page[], nextActive?: number) => void;
}): ReactNode {
  const [renaming, setRenaming] = useState<number | null>(null);
  const update = (index: number, patch: Partial<Page>) =>
    onChange(pages.map((page, at) => (at === index ? { ...page, ...patch } : page)));
  const move = (index: number, by: number) => {
    const target = index + by;
    if (target < 0 || target >= pages.length) return;
    const next = [...pages];
    [next[index], next[target]] = [next[target] as Page, next[index] as Page];
    onChange(next, target);
  };
  const current = pages[active];
  const layout = String(current?.settings.layout ?? 'grid');
  return (
    <nav className="rpl" aria-label="Pages">
      <ol>
        {pages.map((page, index) => (
          <li key={page.id ?? index}>
            {renaming === index ? (
              <input
                className="rpl-rename"
                aria-label="Page name"
                defaultValue={page.title}
                autoFocus
                onBlur={(event) => {
                  const title = event.target.value.trim().slice(0, 120);
                  if (title) update(index, { title });
                  setRenaming(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                  if (event.key === 'Escape') setRenaming(null);
                }}
              />
            ) : (
              <button
                type="button"
                className={`rpl-item${index === active ? ' rpl-item-on' : ''}`}
                aria-current={index === active ? 'page' : undefined}
                onClick={() => onSelect(index)}
                onDoubleClick={() => editable && setRenaming(index)}
                title={editable ? 'Double-click to rename' : undefined}
              >
                <span className="rpl-num mono">{index + 1}</span>
                <span className="rpl-title">{page.title}</span>
                {(page.settings.layout ?? 'grid') !== 'grid' && (
                  <span className="rpl-tag">{LAYOUT_LABEL[String(page.settings.layout)]}</span>
                )}
              </button>
            )}
          </li>
        ))}
      </ol>
      {editable && current && (
        <div className="rpl-tools">
          <label className="rs-field">
            This page shows
            <select
              value={layout}
              onChange={(event) =>
                update(active, {
                  settings: { ...current.settings, layout: event.target.value },
                  widgets: event.target.value === 'grid' ? current.widgets : [],
                })
              }
            >
              <option value="grid">Widgets</option>
              <option value="cover">Cover</option>
              <option value="contents">Contents</option>
            </select>
          </label>
          <div className="rpl-row">
            <button
              type="button"
              className="rb-icon"
              aria-label="Move page up"
              disabled={active === 0}
              onClick={() => move(active, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="rb-icon"
              aria-label="Move page down"
              disabled={active === pages.length - 1}
              onClick={() => move(active, 1)}
            >
              ↓
            </button>
            <button
              type="button"
              className="rb-icon"
              aria-label="Delete this page"
              disabled={pages.length < 2}
              onClick={() =>
                onChange(
                  pages.filter((_, at) => at !== active),
                  Math.max(0, active - 1),
                )
              }
            >
              🗑
            </button>
          </div>
          <button
            type="button"
            className="btn small secondary"
            disabled={pages.length >= 30}
            onClick={() =>
              onChange(
                [
                  ...pages.slice(0, active + 1),
                  { title: `Page ${pages.length + 1}`, settings: { layout: 'grid' }, widgets: [] },
                  ...pages.slice(active + 1),
                ],
                active + 1,
              )
            }
          >
            + Add page after this one
          </button>
        </div>
      )}
    </nav>
  );
}
